import { useState } from "react";
import { formatUsdWithCode, limitAmountToUsd, parseLimitInput } from "./usageFormat";
import { isUsdTwdRateFresh } from "./useUsdTwdRate";

// Each editor owns its entry currency. A display-currency change elsewhere
// must not silently reinterpret an amount someone is already typing.
export function useLimitAmount({ initialUsd = null, initialCurrency = "USD", rateState }) {
  const exchangeRate = rateState?.data;
  const rateReady = Boolean(
    !rateState?.loading && !rateState?.error && isUsdTwdRateFresh(exchangeRate),
  );
  const [currency, setCurrency] = useState(
    initialCurrency === "TWD" && rateReady ? "TWD" : "USD",
  );
  const [text, setText] = useState(() => {
    if (initialUsd === null || initialUsd === undefined) return "";
    return initialCurrency === "TWD" && rateReady
      ? (initialUsd * exchangeRate.rate).toFixed(2)
      : String(initialUsd);
  });
  const parsed = parseLimitInput(text);
  const usdLimit = limitAmountToUsd(text, currency, exchangeRate?.rate);
  const tooSmall = parsed > 0 && usdLimit === 0;
  const canSave =
    usdLimit !== null && !tooSmall && (currency === "USD" || rateReady);

  const changeCurrency = (next) => {
    if (next === currency || (next === "TWD" && !rateReady)) return;
    // Convert the draft instead of reinterpreting its digits in another unit.
    // If a TWD rate has expired, ask for a new USD amount instead.
    const usd = currency === "TWD" && (!rateReady || tooSmall) ? null : usdLimit;
    setText(usd === null ? "" : next === "TWD"
      ? (usd * exchangeRate.rate).toFixed(2)
      : String(usd));
    setCurrency(next);
  };

  return {
    currency, text, setText, changeCurrency, usdLimit, canSave, rateReady,
    isZero: parsed === 0,
    invalid: text !== "" && parsed === null,
    tooSmall,
  };
}

export default function LimitAmountField({ id, label, draft, rateState, saving, language, autoFocus }) {
  const zh = language === "zh";
  const exchangeRate = rateState?.data;
  const twd = draft.currency === "TWD";

  return (
    <fieldset disabled={saving} className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label htmlFor={id} className="text-sm font-semibold text-gray-900 dark:text-white">
          {label} ({draft.currency})
        </label>
        <div
          role="group"
          aria-label={zh ? "額度輸入幣別" : "Limit currency"}
          className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 p-1"
        >
          {["USD", "TWD"].map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => draft.changeCurrency(code)}
              aria-pressed={draft.currency === code}
              disabled={saving || (code === "TWD" && !draft.rateReady)}
              className={`rounded-md px-3 py-1 text-xs font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500 disabled:opacity-40 disabled:cursor-not-allowed ${
                draft.currency === code
                  ? "bg-orange-500 text-white"
                  : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              }`}
            >
              {code}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500 dark:text-gray-400">{twd ? "NT$" : "$"}</span>
        <input
          id={id}
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={draft.text}
          onChange={(event) => draft.setText(event.target.value)}
          aria-describedby={`${id}-hint`}
          aria-invalid={draft.invalid || draft.tooSmall}
          className="min-w-0 flex-1 border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 disabled:opacity-60"
          autoFocus={autoFocus}
        />
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{draft.currency}</span>
      </div>
      <div id={`${id}-hint`} className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
        {twd && exchangeRate && (
          <div className="space-y-1 rounded-lg bg-orange-50 dark:bg-orange-900/20 p-3">
            <p>
              {zh ? "參考匯率" : "Reference rate"} ({exchangeRate.date}): 1 USD = {exchangeRate.rate} TWD
            </p>
            <p role="status" className="font-semibold text-gray-900 dark:text-white">
              {draft.usdLimit !== null
                ? zh
                  ? `將儲存 ${formatUsdWithCode(draft.usdLimit)}（四捨五入至美分）。`
                  : `Will save ${formatUsdWithCode(draft.usdLimit)} (rounded to cents).`
                : zh
                  ? "輸入金額即可預覽將儲存的美元額度。"
                  : "Enter an amount to preview the USD limit that will be saved."}
            </p>
            <p>
              {zh
                ? "系統會儲存並採用這個固定的美元額度，不會固定新臺幣金額；新臺幣等值會隨匯率變動。"
                : "We save and enforce this fixed USD limit. Its TWD equivalent changes with exchange rates; the TWD amount is not fixed."}
            </p>
          </div>
        )}
        {!draft.rateReady && (
          <p>
            {zh
              ? "新臺幣額度輸入需先取得有效的最新匯率；仍可設定美元額度。"
              : "TWD limit entry needs an up-to-date rate. You can still set a USD limit."}
            {rateState?.refresh && (
              <button
                type="button"
                onClick={rateState.refresh}
                disabled={saving || rateState.loading}
                className="ml-1 font-medium underline disabled:opacity-50"
              >
                {rateState.loading
                  ? zh ? "更新中…" : "Updating…"
                  : zh ? "更新匯率" : "Update rate"}
              </button>
            )}
          </p>
        )}
        {draft.invalid && (
          <p className="text-red-600 dark:text-red-400">
            {zh
              ? "請輸入有效的非負金額，最多兩位小數。"
              : "Enter a valid amount of 0 or more, with at most 2 decimals."}
          </p>
        )}
        {draft.tooSmall && (
          <p className="text-red-600 dark:text-red-400">
            {zh
              ? "換算後低於 $0.01 USD，請輸入較大的金額，或明確輸入 0。"
              : "The converted amount is below $0.01 USD. Enter a larger amount, or explicitly enter 0."}
          </p>
        )}
      </div>
    </fieldset>
  );
}
